import {
  Auth0AccountRole,
  MessagesService,
  ReadStateResult,
  UnreadSummary,
} from "client/openapi";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";

/** How often the summary is asked for, while the tab is in view. */
const POLL_MS = 60_000;

/** A read or unread mark this admin just made, for views that hold lists. */
export type ReadChange = {
  messageIds: number[];
  eventIds: number[];
  read: boolean;
};

/**
 * What to mark: messages, the bell's events, whole rows (every unread
 * message of the row as the server knows it, for marking read), and every
 * event this admin hasn't read (Mark all read, however many).
 */
export type Marks = {
  messageIds?: number[];
  eventIds?: number[];
  rows?: string[];
  allEvents?: boolean;
};

type Unread = {
  /** What is unread for this admin; null until loaded, and for non-admins. */
  summary: UnreadSummary | null;
  /** Mark read or unread for this admin; answers with what it marked. */
  setRead: (
    marks: Marks,
    read: boolean
  ) => Promise<ReadStateResult | undefined>;
  /** The latest mark, so lists already on screen show it at once. */
  lastChange: ReadChange | null;
  /** Goes up when a poll finds something new, so open lists reload. */
  polled: number;
};

const UnreadContext = createContext<Unread>({
  summary: null,
  setRead: async () => undefined,
  lastChange: null,
  polled: 0,
});

export function useUnread() {
  return useContext(UnreadContext);
}

// Equal counts can hide a new message in a conversation already unread,
// or a suggested reply saved seconds after its message, so the newest
// inbound message and the newest suggestion count as changes too.
function sameSummary(a: UnreadSummary, b: UnreadSummary) {
  return (
    a.texts === b.texts &&
    a.emails === b.emails &&
    a.events === b.events &&
    a.latest_inbound_id === b.latest_inbound_id &&
    a.latest_suggestion_id === b.latest_suggestion_id
  );
}

/** Conversations unread for this admin (the Communication icon's number). */
export function conversationCount(summary: UnreadSummary | null) {
  return summary ? summary.texts + summary.emails : 0;
}

/** Events unread for this admin (the bell's number). */
export function eventCount(summary: UnreadSummary | null) {
  return summary ? summary.events : 0;
}

/**
 * One source for what is unread for the signed-in admin: polled every minute
 * while the tab is in view and again when it comes back, and updated by every
 * read or unread mark. The bell (events) and the Dashboard's Communication
 * icon (conversations) read it.
 */
export function UnreadProvider({ children }: { children: ReactNode }) {
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);
  const orgId = currently_selected_organization ?? undefined;
  const isAdmin =
    currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
    currently_selected_role === Auth0AccountRole.TADPOLE_ADMIN;

  const [summary, setSummary] = useState<UnreadSummary | null>(null);
  const [lastChange, setLastChange] = useState<ReadChange | null>(null);
  const [polled, setPolled] = useState(0);
  // Only the newest answer counts: a mark supersedes a poll still in flight.
  const latest = useRef(0);
  const shown = useRef<UnreadSummary | null>(null);
  const poll = useRef<() => void>(() => {});
  // Marks go one at a time, so each answer counts every mark before it.
  const marks_ = useRef<Promise<unknown>>(Promise.resolve());

  const show = useCallback((next: UnreadSummary | null, fromPoll: boolean) => {
    const before = shown.current;
    if (fromPoll && before && next && !sameSummary(before, next)) {
      setPolled((n) => n + 1);
    }
    shown.current = next;
    setSummary(next);
  }, []);

  useEffect(() => {
    show(null, false);
    if (!isAdmin || !orgId) return;
    let active = true;
    const load = () => {
      if (document.visibilityState !== "visible") return;
      const request = ++latest.current;
      MessagesService.getOrganizationUnreadSummary({ orgId })
        .then((s) => {
          if (active && request === latest.current) show(s, true);
        })
        .catch(() => {});
    };
    poll.current = load;
    load();
    const timer = window.setInterval(load, POLL_MS);
    document.addEventListener("visibilitychange", load);
    window.addEventListener("focus", load);
    return () => {
      active = false;
      poll.current = () => {};
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", load);
      window.removeEventListener("focus", load);
    };
  }, [isAdmin, orgId, show]);

  const setRead = useCallback(
    (marks: Marks, read: boolean): Promise<ReadStateResult | undefined> => {
      const { messageIds = [], eventIds = [], rows = [], allEvents } = marks;
      if (
        !orgId ||
        (!messageIds.length && !eventIds.length && !rows.length && !allEvents)
      ) {
        return Promise.resolve(undefined);
      }
      const mark = marks_.current
        .catch(() => undefined)
        .then(async () => {
          const request = ++latest.current;
          const result = await MessagesService.setReadState({
            requestBody: {
              org_id: orgId,
              message_ids: messageIds,
              event_ids: eventIds,
              row_keys: rows,
              all_events: allEvents,
              read,
            },
          });
          // A poll that overtook this mark may have missed it: ask again.
          if (request === latest.current) show(result, false);
          else poll.current();
          setLastChange({
            messageIds: result.message_ids ?? [],
            eventIds: result.event_ids ?? [],
            read,
          });
          return result;
        });
      marks_.current = mark;
      return mark;
    },
    [orgId, show]
  );

  const value = useMemo(
    () => ({ summary, setRead, lastChange, polled }),
    [summary, setRead, lastChange, polled]
  );
  return (
    <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>
  );
}
